import React from 'react';
import { Card, Row, Col, Button, Alert } from 'react-bootstrap';
import { FaCheckCircle, FaExternalLinkAlt, FaCopy } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../utils/i18n';
import PublicPageLayout from './PublicPageLayout';
import './GroqTutorial.css';

const GroqTutorial = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [copied, setCopied] = React.useState(false);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <PublicPageLayout
      title={t('groqTutorial.title', language)}
      subtitle={t('groqTutorial.subtitle', language)}
      backTo="/"
      backLabel={t('groqTutorial.back', language)}
    >
      <Alert variant="info" className="mb-4">
        <Alert.Heading>{t('groqTutorial.whyNeededTitle', language)}</Alert.Heading>
        <p className="mb-0">{t('groqTutorial.whyNeeded', language)}</p>
      </Alert>

      <Alert variant="success" className="mb-5">
        <Alert.Heading>
          <FaCheckCircle className="me-2" />
          {t('groqTutorial.freeTitle', language)}
        </Alert.Heading>
        <p className="mb-0">{t('groqTutorial.freeDescription', language)}</p>
      </Alert>

      <Row className="g-4">
        <Col lg={8} className="mx-auto">
          <Card className="mb-4 miskosas-card border-0">
            <Card.Body className="p-4">
              <div className="step-number mb-3">1</div>
              <h3 className="mb-3">{t('groqTutorial.step1Title', language)}</h3>
              <p className="text-muted mb-3">{t('groqTutorial.step1Description', language)}</p>
              <Button
                href="https://console.groq.com/keys"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-miskosas-primary me-2"
              >
                {t('groqTutorial.openConsole', language)} <FaExternalLinkAlt className="ms-2" />
              </Button>
              <p className="small text-muted mt-3 mb-0">
                {t('groqTutorial.step1Note', language)}
              </p>
            </Card.Body>
          </Card>

          <Card className="mb-4 miskosas-card border-0">
            <Card.Body className="p-4">
              <div className="step-number mb-3">2</div>
              <h3 className="mb-3">{t('groqTutorial.step2Title', language)}</h3>
              <p className="text-muted mb-3">{t('groqTutorial.step2Description', language)}</p>
              <div className="code-block mb-3">
                <div className="p-3 bg-light rounded border">
                  <code style={{ fontFamily: 'monospace', fontSize: '0.9rem', color: '#495057' }}>
                    {t('groqTutorial.step2Code', language)}
                  </code>
                </div>
                <Button
                  variant={copied ? 'success' : 'outline-secondary'}
                  size="sm"
                  className="mt-2"
                  onClick={() => copyToClipboard(t('groqTutorial.step2Code', language))}
                >
                  <FaCopy className="me-1" /> {copied ? t('groqTutorial.copied', language) : t('groqTutorial.copy', language)}
                </Button>
              </div>
            </Card.Body>
          </Card>

          <Card className="mb-4 miskosas-card border-0">
            <Card.Body className="p-4">
              <div className="step-number mb-3">3</div>
              <h3 className="mb-3">{t('groqTutorial.step3Title', language)}</h3>
              <p className="text-muted mb-3">{t('groqTutorial.step3Description', language)}</p>
              <ol className="ps-4">
                <li className="mb-2">{t('groqTutorial.step3Item1', language)}</li>
                <li className="mb-2">{t('groqTutorial.step3Item2', language)}</li>
                <li className="mb-2">{t('groqTutorial.step3Item3', language)}</li>
              </ol>
              <Button
                className="btn-miskosas-primary mt-3"
                onClick={() => navigate('/app#settings')}
              >
                {t('groqTutorial.goToSettings', language)}
              </Button>
            </Card.Body>
          </Card>

          <Card className="mb-4 miskosas-card border-0">
            <Card.Body className="p-4">
              <h3 className="mb-3">{t('groqTutorial.faqTitle', language)}</h3>
              <div className="faq-item mb-4">
                <h5 className="fw-bold">{t('groqTutorial.faq1Question', language)}</h5>
                <p className="text-muted mb-0">{t('groqTutorial.faq1Answer', language)}</p>
              </div>
              <div className="faq-item mb-4">
                <h5 className="fw-bold">{t('groqTutorial.faq2Question', language)}</h5>
                <p className="text-muted mb-0">{t('groqTutorial.faq2Answer', language)}</p>
              </div>
              <div className="faq-item mb-4">
                <h5 className="fw-bold">{t('groqTutorial.faq3Question', language)}</h5>
                <p className="text-muted mb-0">{t('groqTutorial.faq3Answer', language)}</p>
              </div>
              <div className="faq-item">
                <h5 className="fw-bold">{t('groqTutorial.faq4Question', language)}</h5>
                <p className="text-muted mb-0">{t('groqTutorial.faq4Answer', language)}</p>
              </div>
            </Card.Body>
          </Card>

          <Card className="border-success miskosas-card">
            <Card.Body className="p-4 text-center">
              <h4 className="mb-3">{t('groqTutorial.readyTitle', language)}</h4>
              <p className="text-muted mb-4">{t('groqTutorial.readyDescription', language)}</p>
              <Button
                className="btn-miskosas-primary"
                size="lg"
                onClick={() => navigate('/app#settings')}
              >
                {t('groqTutorial.configureNow', language)}
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </PublicPageLayout>
  );
};

export default GroqTutorial;
